import React, { useCallback, useEffect, useMemo, useState } from "react";
import BotStatusToggle from "components/BotStatusToggle";
import { Drawer, Modal, message } from "antd";
import axios from "util/Api";
import API_ENDPOINTS from "util/apiEndpoints";
import { useEmployeePanelAccess } from "util/employeePanelAccess";

const PAGE_SIZE = 30;

const STATUS_MAP = {
  open: { label: "Acik", badge: "bg-p" },
  in_progress: { label: "Islemde", badge: "bg-b" },
  waiting_customer: { label: "Beklemede", badge: "bg-y" },
  resolved: { label: "Cozuldu", badge: "bg-g" },
  closed: { label: "Kapatildi", badge: "bg-gr" },
};

const PRIORITY_MAP = {
  low: { label: "Dusuk", badge: "bg-gr" },
  medium: { label: "Normal", badge: "bg-b" },
  high: { label: "Yuksek", badge: "bg-o" },
  urgent: { label: "Acil", badge: "bg-r" },
};

const DEFAULT_TAGS = [
  { label: "acil", badge: "bg-r" },
  { label: "escalation", badge: "bg-o" },
  { label: "vip-musteri", badge: "bg-p" },
  { label: "tekrar-eden", badge: "bg-b" },
  { label: "cozuldu", badge: "bg-g" },
  { label: "bekleniyor", badge: "bg-y" },
  { label: "fatura", badge: "bg-p" },
  { label: "teknik", badge: "bg-b" },
  { label: "siparis", badge: "bg-o" },
  { label: "sla-riski", badge: "bg-r" },
];

const DEFAULT_CATEGORIES = [
  { key: "Fatura / Odeme", icon: "💳", sla: "2 saat", description: "Odeme ve tahsilat sorunlari" },
  { key: "Teknik Destek", icon: "🔧", sla: "4 saat", description: "Urun, servis ve entegrasyon sorunlari" },
  { key: "Siparis / Teslimat", icon: "📦", sla: "3 saat", description: "Siparis ve teslimat takibi" },
  { key: "Iade / Degisim", icon: "🔄", sla: "6 saat", description: "Iade, degisim ve iptal islemleri" },
  { key: "Genel Bilgi", icon: "📋", sla: "8 saat", description: "Bilgilendirme ve genel destek" },
  { key: "Guvenlik / Hesap", icon: "🛡️", sla: "1 saat", description: "Hesap guvenligi ve erisim sorunlari" },
];

const DEFAULT_CANNED_RESPONSES = [
  { id: "welcome", title: "Hosgeldiniz Mesaji", category: "Karsilama", badge: "bg-g", shortcut: "/hosgeldin", text: "Merhaba! Bize ulastiginiz icin tesekkur ederiz. Talebinizi inceliyoruz ve en kisa surede size donus yapacagiz." },
  { id: "invoice", title: "Odeme Sorunu Bilgilendirme", category: "Fatura", badge: "bg-r", shortcut: "/odeme-sorun", text: "Odeme isleminizdeki sorunu inceliyoruz. Kisa sure icinde size net bir guncelleme paylasacagiz." },
  { id: "refund", title: "Iade Sureci Bilgilendirme", category: "Iade", badge: "bg-o", shortcut: "/iade-bilgi", text: "Iade talebiniz alindi. Kontroller tamamlandiginda size tekrar bilgi verecegiz." },
  { id: "technical", title: "Teknik Destek Yonlendirme", category: "Teknik", badge: "bg-b", shortcut: "/teknik-destek", text: "Yasadiginiz teknik sorunu teknik ekibimize ilettik. Cevap alir almaz sizinle paylasacagiz." },
  { id: "closing", title: "Ticket Kapanma Mesaji", category: "Kapanis", badge: "bg-gr", shortcut: "/kapanis", text: "Talebiniz cozulmustur. Baska bir konuda yardimci olmamizi isterseniz tekrar yazabilirsiniz." },
];

const DEFAULT_FORM = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  subject: "",
  description: "",
  category: "",
  priority: "medium",
  status: "open",
  resolution_text: "",
  assigned_user_id: "",
  tags_input: "",
  source: "panel",
};

const DEFAULT_CATEGORY_FORM = {
  key: "",
  icon: "📁",
  sla: "4 saat",
  description: "",
};

const DEFAULT_CANNED_FORM = {
  id: "",
  title: "",
  category: "Genel",
  shortcut: "",
  text: "",
  badge: "bg-p",
};

const DEFAULT_SETTINGS_FORM = {
  slaHoursByPriority: {
    urgent: 1,
    high: 2,
    medium: 4,
    low: 8,
  },
  firstResponseMinutes: 15,
  escalationPercent: 80,
  autoAssign: true,
  roundRobin: true,
  skillRouting: false,
  loadBalancing: true,
  vipPrioritization: true,
  notifyNewTicket: true,
  notifySla: true,
  notifyEscalation: true,
  notifyCustomerReply: true,
  notifyCsat: false,
  dailySummaryEmail: true,
  weekdayStart: "09:00",
  weekdayEnd: "18:00",
  saturdayStart: "10:00",
  saturdayEnd: "14:00",
  sundayClosed: true,
  afterHoursBot: true,
  holidayAutoReply: true,
  timezone: "UTC+3 (Istanbul)",
};

const CAT_COLORS = ["#8b5cf6", "var(--cp-info)", "#fb923c", "var(--cp-warn)", "var(--cp-danger)", "var(--cp-muted)", "var(--cp-ok)", "#22d3ee"];

const SUPPORT_PANEL_CSS = `
.sup-page{position:relative;z-index:1;max-width:1440px;margin:0 auto;padding:28px 24px;color:var(--cp-text)}
.sup-page *{box-sizing:border-box}
.sup-page::before{content:'';position:fixed;inset:0;background:radial-gradient(ellipse at 50% 30%,rgba(139,92,246,.06) 0%,transparent 55%);pointer-events:none}
.sup-shell{position:relative;z-index:1}
.sup-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:28px;flex-wrap:wrap;gap:12px}
.sup-hdr-l{display:flex;align-items:center;gap:14px}
.sup-av{width:48px;height:48px;border-radius:13px;background:linear-gradient(135deg,#8b5cf6,#6366f1);display:flex;align-items:center;justify-content:center;font-size:24px;box-shadow:0 0 25px rgba(139,92,246,.25)}
.sup-hdr h1{font-size:22px;font-weight:800;color:var(--cp-title);margin:0}
.sup-hdr p{font-size:11px;color:var(--cp-faint);margin:1px 0 0}
.sup-hdr-r{display:flex;gap:8px;flex-wrap:wrap}
.sup-btn{padding:8px 16px;border-radius:9px;border:1px solid rgba(139,92,246,.2);background:rgba(139,92,246,.07);color:var(--cp-violet);font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;font-family:inherit}
.sup-btn:hover{background:rgba(139,92,246,.14)}
.sup-btn:disabled{opacity:.55;cursor:not-allowed}
.sup-btn-primary{background:linear-gradient(135deg,#8b5cf6,#6366f1);color:#fff;border:none}
.sup-btn-primary:hover{box-shadow:0 4px 18px rgba(139,92,246,.35)}
.sup-btn-danger{border-color:rgba(248,113,113,.25);background:rgba(248,113,113,.07);color:var(--cp-danger)}
.sup-btn-danger:hover{background:rgba(248,113,113,.14)}
.sup-btn-warn{border-color:rgba(251,191,36,.25);background:rgba(251,191,36,.07);color:var(--cp-warn)}
.sup-btn-green{border-color:rgba(74,222,128,.25);background:rgba(74,222,128,.07);color:var(--cp-ok)}
.sup-btn-sm{padding:5px 10px;font-size:11px;border-radius:7px}
.sup-alert{display:flex;align-items:center;gap:10px;padding:12px 16px;border-radius:10px;margin-bottom:18px;font-size:12px;font-weight:500}
.sup-alert-w{background:rgba(248,113,113,.06);border:1px solid rgba(248,113,113,.12);color:var(--cp-danger)}
.sup-alert-s{background:rgba(139,92,246,.06);border:1px solid rgba(139,92,246,.12);color:var(--cp-violet)}
.sup-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
.sup-stat{background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.05);border-radius:12px;padding:16px;text-align:center}
.sup-stat-i{font-size:22px;margin-bottom:6px}
.sup-stat-v{font-size:24px;font-weight:800;color:var(--cp-title)}
.sup-stat-l{font-size:10px;color:var(--cp-faint);margin-top:3px}
.sup-stat-c{font-size:10px;font-weight:600;margin-top:4px;color:var(--cp-ok)}
.sup-tabs{display:flex;gap:2px;margin-bottom:18px;border-bottom:1px solid rgba(255,255,255,.05);overflow:auto}
.sup-tab{padding:9px 16px;font-size:12px;font-weight:600;color:var(--cp-faint);cursor:pointer;border-bottom:2px solid transparent;transition:all .2s;white-space:nowrap}
.sup-tab:hover{color:var(--cp-violet)}
.sup-tab.active{color:var(--cp-violet);border-bottom-color:#8b5cf6}
.sup-card{background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.05);border-radius:12px;padding:18px;margin-bottom:14px}
.sup-card-title{font-size:13px;font-weight:700;margin-bottom:14px;display:flex;align-items:center;justify-content:space-between;gap:12px}
.sup-card-title span{display:flex;align-items:center;gap:6px}
.sup-srch{display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap}
.sup-input,.sup-fi{width:100%;padding:9px 13px;border-radius:9px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.025);color:var(--cp-text);font-size:12px;font-family:inherit;outline:none}
.sup-input:focus,.sup-fi:focus,.sup-ft:focus,.sup-fs:focus{border-color:#8b5cf6}
.sup-select,.sup-fs{padding:9px 13px;border-radius:9px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.025);color:var(--cp-text);font-size:12px;font-family:inherit;cursor:pointer;appearance:none;outline:none}
.sup-select option,.sup-fs option{background:var(--cp-border)}
.sup-textarea,.sup-ft{width:100%;padding:9px 13px;border-radius:9px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.025);color:var(--cp-text);font-size:12px;font-family:inherit;outline:none;min-height:70px;resize:vertical}
.sup-g2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px}
.sup-g3{display:grid;grid-template-columns:2fr 1fr;gap:12px;margin-bottom:14px}
.sup-table{width:100%;border-collapse:collapse}
.sup-table th{text-align:left;font-size:10px;color:var(--cp-faint);font-weight:600;padding:7px 8px;border-bottom:1px solid rgba(255,255,255,.05);text-transform:uppercase;letter-spacing:.4px}
.sup-table td{font-size:12px;padding:8px;border-bottom:1px solid rgba(255,255,255,.03)}
.sup-table tr:hover td{background:rgba(139,92,246,.02)}
.sup-row-click{cursor:pointer}
.sup-badge{font-size:10px;padding:2px 7px;border-radius:5px;font-weight:600;display:inline-block}
.bg-g{background:rgba(74,222,128,.1);color:var(--cp-ok)}.bg-y{background:rgba(251,191,36,.1);color:var(--cp-warn)}.bg-b{background:rgba(59,130,246,.1);color:var(--cp-info)}.bg-r{background:rgba(248,113,113,.1);color:var(--cp-danger)}.bg-p{background:rgba(139,92,246,.1);color:var(--cp-violet)}.bg-o{background:rgba(251,146,60,.1);color:#fb923c}.bg-gr{background:rgba(148,163,184,.1);color:var(--cp-muted)}
.sup-ai{background:rgba(139,92,246,.03);border:1px solid rgba(139,92,246,.1);border-radius:9px;padding:12px;margin-bottom:8px}
.sup-ai:last-child{margin:0}
.sup-ai-b{font-size:9px;padding:2px 6px;border-radius:4px;font-weight:700;background:rgba(139,92,246,.18);color:var(--cp-violet);text-transform:uppercase;display:inline-block;margin-bottom:4px}
.sup-ai-t{font-size:11px;color:var(--cp-text2);line-height:1.5}
.sup-dr{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid rgba(255,255,255,.025);font-size:11px;gap:12px}
.sup-dr .l{color:var(--cp-faint)}.sup-dr .v{color:var(--cp-text);font-weight:600;text-align:right}
.sup-drw-s{margin-bottom:18px}
.sup-drw-s h3{font-size:11px;font-weight:700;color:#8b5cf6;text-transform:uppercase;letter-spacing:.4px;margin-bottom:8px}
.sup-conv{margin-bottom:14px}
.sup-msg{padding:10px 14px;border-radius:10px;margin-bottom:8px;font-size:12px;line-height:1.6;max-width:85%}
.sup-msg-c{background:rgba(59,130,246,.08);border:1px solid rgba(59,130,246,.12);margin-right:auto}
.sup-msg-a{background:rgba(139,92,246,.08);border:1px solid rgba(139,92,246,.12);margin-left:auto}
.sup-msg-h{font-size:10px;font-weight:600;margin-bottom:3px;display:flex;justify-content:space-between}
.sup-msg-h .n{color:var(--cp-violet)}.sup-msg-h .t{color:#475569}
.sup-sla-bar{height:6px;border-radius:3px;background:rgba(255,255,255,.05);overflow:hidden;margin-top:6px}
.sup-sla-fill{height:100%;border-radius:3px;transition:width .3s}
.sup-sla-ok{background:var(--cp-ok)}
.sup-sla-warn{background:var(--cp-warn)}
.sup-sla-danger{background:var(--cp-danger)}
.sup-cr-item{background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.04);border-radius:10px;padding:14px;margin-bottom:8px;transition:all .2s}
.sup-cr-item:hover{border-color:rgba(139,92,246,.15)}
.sup-cr-title{font-size:12px;font-weight:700;margin-bottom:4px;display:flex;justify-content:space-between;align-items:center;gap:10px}
.sup-cr-cat{font-size:10px;color:var(--cp-faint);margin-bottom:6px}
.sup-cr-text{font-size:11px;color:var(--cp-muted);line-height:1.5}
.sup-cr-actions{display:flex;gap:4px;margin-top:8px;flex-wrap:wrap}
.sup-cat-item{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-radius:10px;background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.04);margin-bottom:8px;gap:12px}
.sup-cat-item:hover{border-color:rgba(139,92,246,.12)}
.sup-cat-info{display:flex;align-items:center;gap:10px}
.sup-cat-icon{width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:16px;background:rgba(139,92,246,.08)}
.sup-cat-name{font-size:12px;font-weight:600}
.sup-cat-count{font-size:10px;color:var(--cp-faint);margin-top:1px}
.sup-cat-actions{display:flex;gap:4px}
.sup-set{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.sup-set-g{background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.04);border-radius:10px;padding:16px}
.sup-set-h{font-size:12px;font-weight:700;margin-bottom:12px;display:flex;align-items:center;gap:6px}
.sup-set-r{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid rgba(255,255,255,.03);font-size:11px;gap:12px}
.sup-set-r:last-child{border:none}
.sup-set-r .sl{color:var(--cp-muted)}.sup-set-r .sv{color:var(--cp-text);font-weight:600}
.sup-tog{width:36px;height:20px;border-radius:10px;background:rgba(255,255,255,.1);position:relative;cursor:pointer;transition:all .2s;border:none;flex-shrink:0}
.sup-tog.on{background:rgba(139,92,246,.4)}
.sup-tog::after{content:'';position:absolute;width:16px;height:16px;border-radius:8px;background:#fff;top:2px;left:2px;transition:all .2s}
.sup-tog.on::after{left:18px}
.sup-del-i{width:48px;height:48px;border-radius:14px;background:rgba(248,113,113,.08);display:flex;align-items:center;justify-content:center;font-size:24px;margin:0 auto 12px}
.sup-form-group{margin-bottom:14px}
.sup-form-label{display:block;font-size:10px;font-weight:600;color:var(--cp-muted);margin-bottom:5px;text-transform:uppercase;letter-spacing:.3px}
.sup-form-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.sup-empty{padding:24px;border:1px dashed rgba(139,92,246,.2);border-radius:12px;color:var(--cp-muted);text-align:center}
.sup-note{font-size:10px;color:var(--cp-faint);margin-top:4px}
.sup-pager{display:flex;justify-content:space-between;align-items:center;gap:12px;padding-top:14px;flex-wrap:wrap}
.sup-pager-meta{font-size:11px;color:var(--cp-muted)}
.sup-pager-actions{display:flex;gap:8px}
.sup-modal .ant-modal-content,.sup-drawer .ant-drawer-content,.sup-drawer .ant-drawer-content-wrapper,.sup-drawer .ant-drawer-wrapper-body,.sup-drawer .ant-drawer-body{background:var(--cp-elevated);color:var(--cp-text);border-color:rgba(139,92,246,.12)}
.sup-modal .ant-modal-header,.sup-drawer .ant-drawer-header{background:var(--cp-elevated);border-bottom:1px solid rgba(255,255,255,.05)}
.sup-drawer.ant-drawer .ant-drawer-mask{background:transparent}
.sup-drawer.ant-drawer .ant-drawer-content-wrapper{box-shadow:-12px 0 36px rgba(0,0,0,.35)}
.sup-modal .ant-modal-title,.sup-drawer .ant-drawer-title,.sup-drawer .ant-drawer-close{color:var(--cp-text)}
.sup-modal .ant-modal-footer{border-top:1px solid rgba(255,255,255,.05)}
@media(max-width:1100px){.sup-stats{grid-template-columns:repeat(2,1fr)}.sup-g2,.sup-g3,.sup-set{grid-template-columns:1fr}}
@media(max-width:768px){.sup-stats{grid-template-columns:1fr}.sup-form-row{grid-template-columns:1fr}}
`;

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("tr-TR");
  } catch (_error) {
    return value;
  }
};

const formatMinutes = (minutes) => {
  const value = Number(minutes || 0);
  if (!value) return "—";
  if (value < 60) return `${value}dk`;
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  return mins ? `${hours}s ${mins}dk` : `${hours}s`;
};

const formatSignedDelta = (value) => {
  const number = Number(value || 0);
  if (!number) return "0";
  return `${number > 0 ? "+" : ""}${number}`;
};

const hourDiffLabel = (value) => {
  if (!value) return "—";
  const ms = new Date(value).getTime() - Date.now();
  if (ms <= 0) return "Gecti";
  const hours = Math.max(0, Math.floor(ms / 3600000));
  const minutes = Math.max(0, Math.floor((ms % 3600000) / 60000));
  if (hours > 0) return `${hours}s ${minutes}dk`;
  return `${minutes}dk`;
};

const slaPercent = (value) => {
  if (!value) return 0;
  const ms = new Date(value).getTime() - Date.now();
  if (ms <= 0) return 100;
  const totalMs = 3600000;
  return Math.min(100, Math.max(0, Math.round(((totalMs - ms) / totalMs) * 100)));
};

const slaColorClass = (pct) => {
  if (pct >= 75) return "sup-sla-danger";
  if (pct >= 50) return "sup-sla-warn";
  return "sup-sla-ok";
};

const slaTextColor = (row) => {
  if (!row) return "var(--cp-ok)";
  if (row.status === "closed" || row.status === "resolved") return "var(--cp-muted)";
  if (row.sla_breached) return "var(--cp-danger)";
  const pct = slaPercent(row.resolution_due_at);
  if (pct >= 75) return "var(--cp-danger)";
  if (pct >= 50) return "var(--cp-warn)";
  return "var(--cp-ok)";
};

const slaDisplayText = (row) => {
  if (!row) return "—";
  if (row.status === "closed") return "Kapali";
  if (row.status === "resolved") return "Tamam";
  return hourDiffLabel(row.resolution_due_at);
};

const escapeCsvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const downloadCsv = (rows, filename) => {
  const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
};

const normalizeTags = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((tag) => String(tag || "").trim())
    .filter(Boolean);

const buildTenantSettings = (rows) => {
  const list = Array.isArray(rows) ? rows.filter((row) => row && typeof row === "object") : [];
  if (!list.length) return null;
  const sorted = [...list].sort((left, right) => Number(left.id || 0) - Number(right.id || 0));
  return sorted.reduce((merged, row) => ({
    ...merged,
    ...row,
    support_panel_config_json: {
      ...(merged.support_panel_config_json && typeof merged.support_panel_config_json === "object" ? merged.support_panel_config_json : {}),
      ...(row.support_panel_config_json && typeof row.support_panel_config_json === "object" ? row.support_panel_config_json : {}),
    },
  }), {});
};

const buildSettingsForm = (settings) => {
  const config =
    settings && settings.support_panel_config_json && typeof settings.support_panel_config_json === "object"
      ? settings.support_panel_config_json
      : {};
  return {
    ...DEFAULT_SETTINGS_FORM,
    ...config,
    slaHoursByPriority: {
      ...DEFAULT_SETTINGS_FORM.slaHoursByPriority,
      ...(config.slaHoursByPriority && typeof config.slaHoursByPriority === "object" ? config.slaHoursByPriority : {}),
    },
  };
};

const buildCategories = (settings, stats) => {
  const configured = Array.isArray(settings && settings.support_categories_json) && settings.support_categories_json.length
    ? settings.support_categories_json
    : DEFAULT_CATEGORIES;
  const counts = (stats && stats.by_category) || {};
  return configured.map((category, index) => ({
    ...category,
    count: Number(counts[category.key] || 0),
    color: CAT_COLORS[index % CAT_COLORS.length],
  }));
};

const buildCannedResponses = (settings) => {
  const configured = settings && Array.isArray(settings.support_canned_responses_json) && settings.support_canned_responses_json.length
    ? settings.support_canned_responses_json
    : DEFAULT_CANNED_RESPONSES;
  return configured.map((item, index) => ({
    badge: item.badge || ["bg-g", "bg-r", "bg-o", "bg-b", "bg-gr"][index % 5],
    ...item,
    id: item.id || `cr-${index + 1}`,
  }));
};

const Toggle = ({ value, onChange }) => (
  <button className={`sup-tog${value ? " on" : ""}`} onClick={() => onChange(!value)} type="button" />
);

const SupportTickets = () => {
  const accessMeta = useEmployeePanelAccess({ panelKey: "support-tickets" });
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  // DERİN BAĞLANTI — sektör kartı bir ALT KÜME vaat ediyorsa ("açık talepler")
  // hedef o alt kümeyle açılmalı; filtresiz liste kartı yalancı çıkarır.
  const cardFilter = (() => {
    try { return new URLSearchParams(window.location.search).get("status"); }
    catch (_e) { return null; }
  })();
  const [statusFilter, setStatusFilter] = useState(["open", "in_progress", "resolved", "closed"].indexOf(cardFilter) !== -1 ? cardFilter : "all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("tickets");
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [ticketDeleteOpen, setTicketDeleteOpen] = useState(false);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catDeleteOpen, setCatDeleteOpen] = useState(false);
  const [crModalOpen, setCrModalOpen] = useState(false);
  const [crDeleteOpen, setCrDeleteOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [replyText, setReplyText] = useState("");
  const [replyPickerOpen, setReplyPickerOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState(DEFAULT_SETTINGS_FORM);
  const [categoryForm, setCategoryForm] = useState(DEFAULT_CATEGORY_FORM);
  const [cannedForm, setCannedForm] = useState(DEFAULT_CANNED_FORM);
  const [editingCategoryKey, setEditingCategoryKey] = useState("");
  const [editingCannedId, setEditingCannedId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [cannedSearch, setCannedSearch] = useState("");
  const [cannedFilter, setCannedFilter] = useState("all");

  const updateFormField = useCallback((field, parser) => (eventOrValue) => {
    const rawValue = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
    const value = parser ? parser(rawValue) : rawValue;
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateSettingsField = useCallback((field, parser) => (eventOrValue) => {
    const rawValue = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
    const value = parser ? parser(rawValue) : rawValue;
    setSettingsForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateCategoryField = useCallback((field) => (eventOrValue) => {
    const value = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
    setCategoryForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateCannedField = useCallback((field) => (eventOrValue) => {
    const value = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
    setCannedForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const upsertTenantSettings = useCallback((payload) => {
    if (settings && settings.id) return axios.post(API_ENDPOINTS.tenantSettings.update, { id: settings.id, ...payload });
    return axios.post(API_ENDPOINTS.tenantSettings.create, payload);
  }, [settings]);

  const loadData = useCallback(() => {
    setLoading(true);
    const query = new URLSearchParams({ page: "1", limit: "240" });
    if (statusFilter !== "all") query.set("status", statusFilter);
    if (priorityFilter !== "all") query.set("priority", priorityFilter);
    if (categoryFilter !== "all") query.set("category", categoryFilter);
    if (search.trim()) query.set("q", search.trim());

    Promise.allSettled([
      axios.get(`${API_ENDPOINTS.supportTickets.list}?${query.toString()}`),
      axios.get(API_ENDPOINTS.supportTickets.stats),
      axios.get(`${API_ENDPOINTS.supportTickets.report}?period=month`),
      axios.post(API_ENDPOINTS.users.list, {}),
      axios.post(API_ENDPOINTS.tenantSettings.list, {}),
    ])
      .then((results) => {
        const listResponse = results[0] && results[0].status === "fulfilled" ? results[0].value : null;
        const statsResponse = results[1] && results[1].status === "fulfilled" ? results[1].value : null;
        const reportResponse = results[2] && results[2].status === "fulfilled" ? results[2].value : null;
        const usersResponse = results[3] && results[3].status === "fulfilled" ? results[3].value : null;
        const settingsResponse = results[4] && results[4].status === "fulfilled" ? results[4].value : null;
        const successCount = [listResponse, statsResponse, reportResponse, usersResponse, settingsResponse].filter(Boolean).length;
        if (!successCount) throw new Error("Destek listesi yuklenemedi");

        setRows((listResponse && listResponse.data && listResponse.data.data) || []);
        setStats((statsResponse && statsResponse.data && statsResponse.data.data) || {});
        setReport((reportResponse && reportResponse.data && reportResponse.data.data) || {});
        setUsers((usersResponse && usersResponse.data && usersResponse.data.data) || []);
        setSettings(buildTenantSettings((settingsResponse && settingsResponse.data && settingsResponse.data.data) || []));
      })
      .catch((error) => {
        message.error((error && error.response && error.response.data && error.response.data.message) || "Destek verileri yuklenemedi");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [categoryFilter, priorityFilter, search, statusFilter]);

  useEffect(() => {
    if (!accessMeta.loaded) return;
    loadData();
  }, [accessMeta.loaded, loadData]);

  useEffect(() => {
    setSettingsForm(buildSettingsForm(settings));
  }, [settings]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, priorityFilter, categoryFilter]);

  const categoryList = useMemo(() => buildCategories(settings, stats), [settings, stats]);
  const cannedResponses = useMemo(() => buildCannedResponses(settings), [settings]);

  const filteredRows = useMemo(() => {
    const text = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (priorityFilter !== "all" && row.priority !== priorityFilter) return false;
      if (categoryFilter !== "all" && row.category !== categoryFilter) return false;
      if (!text) return true;
      const haystack = [
        row.subject,
        row.description,
        row.category,
        row.resolution_text,
        row.customer_name,
        row.customer_email,
        row.customer_phone,
        row.contact && row.contact.display_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(text);
    });
  }, [categoryFilter, priorityFilter, rows, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const selectedRow = rows.find((row) => Number(row.id) === Number(selectedId)) || paginatedRows[0] || filteredRows[0] || null;

  const slaBreachedCount = useMemo(
    () => rows.filter((row) => row.sla_breached || (row.resolution_due_at && new Date(row.resolution_due_at).getTime() < Date.now() && !["resolved", "closed"].includes(row.status))).length,
    [rows]
  );
  const invoiceTicketCount = useMemo(() => rows.filter((row) => String(row.category || "").toLowerCase().includes("fatura")).length, [rows]);
  const urgentCount = useMemo(() => rows.filter((row) => row.priority === "urgent" && ["open", "in_progress"].includes(row.status)).length, [rows]);

  const tags = useMemo(() => {
    const seen = new Map();
    DEFAULT_TAGS.forEach((tag) => seen.set(tag.label, tag));
    rows.forEach((row) => {
      normalizeTags(row.tags_json).forEach((tag) => {
        if (!seen.has(tag)) seen.set(tag, { label: tag, badge: "bg-p" });
      });
    });
    return Array.from(seen.values());
  }, [rows]);

  const filteredCategories = useMemo(() => {
    const text = categorySearch.trim().toLowerCase();
    if (!text) return categoryList;
    return categoryList.filter((category) =>
      [category.key, category.description, category.sla].filter(Boolean).join(" ").toLowerCase().includes(text)
    );
  }, [categoryList, categorySearch]);

  const filteredCannedResponses = useMemo(() => {
    const text = cannedSearch.trim().toLowerCase();
    return cannedResponses.filter((item) => {
      if (cannedFilter !== "all" && item.category !== cannedFilter) return false;
      if (!text) return true;
      return [item.title, item.category, item.shortcut, item.text].filter(Boolean).join(" ").toLowerCase().includes(text);
    });
  }, [cannedFilter, cannedResponses, cannedSearch]);

  const assignedExperts = useMemo(
    () => users.filter((user) => String(user.status || "active") === "active"),
    [users]
  );

  const groupedCannedResponses = useMemo(() => {
    const left = [];
    const right = [];
    filteredCannedResponses.forEach((item, index) => {
      if (index % 2 === 0) left.push(item);
      else right.push(item);
    });
    return [left, right];
  }, [filteredCannedResponses]);

  const openNew = () => {
    setEditingTicketId(null);
    setForm(DEFAULT_FORM);
    setNewOpen(true);
  };

  const openEdit = (row) => {
    setSelectedId(row.id);
    setEditingTicketId(row.id);
    setForm({
      customer_name: row.customer_name || (row.contact && row.contact.display_name) || "",
      customer_email: row.customer_email || (row.contact && row.contact.email) || "",
      customer_phone: row.customer_phone || (row.contact && (row.contact.phone_e164 || row.contact.wa_id)) || "",
      subject: row.subject || "",
      description: row.description || "",
      category: row.category || "",
      priority: row.priority || "medium",
      status: row.status || "open",
      resolution_text: row.resolution_text || "",
      assigned_user_id: row.assigned_user_id || "",
      tags_input: normalizeTags(row.tags_json).join(", "),
      source: row.source || "panel",
    });
    setEditOpen(true);
  };

  const openCategoryModal = (category) => {
    if (category) {
      setEditingCategoryKey(category.key);
      setCategoryForm({
        key: category.key || "",
        icon: category.icon || "📁",
        sla: category.sla || "4 saat",
        description: category.description || "",
      });
    } else {
      setEditingCategoryKey("");
      setCategoryForm(DEFAULT_CATEGORY_FORM);
    }
    setCatModalOpen(true);
  };

  const openCannedModal = (item) => {
    if (item) {
      setEditingCannedId(item.id);
      setCannedForm({
        id: item.id,
        title: item.title || "",
        category: item.category || "Genel",
        shortcut: item.shortcut || "",
        text: item.text || "",
        badge: item.badge || "bg-p",
      });
    } else {
      setEditingCannedId("");
      setCannedForm(DEFAULT_CANNED_FORM);
    }
    setCrModalOpen(true);
  };

  const buildSupportSettingsPayload = useCallback((patch = {}) => {
    const nextPanelConfig = patch.support_panel_config_json || settingsForm;
    const nextCategories = patch.support_categories_json || categoryList.map(({ color, count, ...item }) => item);
    const nextCanned = patch.support_canned_responses_json || cannedResponses.map(({ count, color, ...item }) => item);
    return {
      support_panel_config_json: nextPanelConfig,
      support_categories_json: nextCategories,
      support_categories: JSON.stringify(nextCategories.map((item) => item.key)),
      support_canned_responses_json: nextCanned,
      support_sla_resolution_hours: Number(nextPanelConfig.slaHoursByPriority && nextPanelConfig.slaHoursByPriority.medium) || 4,
    };
  }, [cannedResponses, categoryList, settingsForm]);

  const saveSupportSettings = async (patch = {}, successText = "Ayarlar kaydedildi") => {
    const payload = buildSupportSettingsPayload(patch);
    await upsertTenantSettings(payload);
    setSettings((prev) => ({
      ...(prev || {}),
      support_panel_config_json: payload.support_panel_config_json,
      support_categories_json: payload.support_categories_json,
      support_categories: payload.support_categories,
      support_canned_responses_json: payload.support_canned_responses_json,
      support_sla_resolution_hours: payload.support_sla_resolution_hours,
    }));
    message.success(successText);
  };

  const saveTicket = async (mode) => {
    if (!form.subject || !form.description) {
      message.error("Konu ve aciklama zorunlu");
      return;
    }
    const payload = {
      customer_name: form.customer_name || null,
      customer_email: form.customer_email || null,
      customer_phone: form.customer_phone || null,
      subject: form.subject,
      description: form.description,
      category: form.category || "Genel Bilgi",
      priority: form.priority || "medium",
      status: form.status || "open",
      resolution_text: form.resolution_text || null,
      source: "panel",
      created_by: "panel",
      assigned_user_id: form.assigned_user_id || null,
      tags_json: normalizeTags(form.tags_input),
    };
    try {
      if (mode === "edit") await axios.post(API_ENDPOINTS.supportTickets.update, { id: editingTicketId || selectedId, ...payload });
      else await axios.post(API_ENDPOINTS.supportTickets.create, payload);
      message.success(mode === "edit" ? "Ticket guncellendi" : "Ticket olusturuldu");
      setNewOpen(false);
      setEditOpen(false);
      setEditingTicketId(null);
      loadData();
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Ticket kaydedilemedi");
    }
  };

  const updateStatus = async (row, status, text) => {
    try {
      await axios.post(API_ENDPOINTS.supportTickets.updateStatus, { id: row.id, status });
      message.success(text);
      setDetailOpen(false);
      loadData();
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Ticket guncellenemedi");
    }
  };

  const escalateTicket = async (row) => {
    try {
      await axios.post(API_ENDPOINTS.supportTickets.update, {
        id: row.id,
        priority: "urgent",
        status: "in_progress",
        resolution_text: [row.resolution_text, "Eskalasyon: Ust seviyeye aktarildi."].filter(Boolean).join("\n"),
      });
      message.success("Ticket eskalasyon kuyruğuna alindi");
      loadData();
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Eskalasyon basarisiz");
    }
  };

  const deleteTicket = async () => {
    if (!selectedRow) return;
    try {
      await axios.post(API_ENDPOINTS.supportTickets.delete, { id: selectedRow.id });
      message.success("Ticket silindi");
      setTicketDeleteOpen(false);
      setDetailOpen(false);
      setSelectedId(null);
      loadData();
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Ticket silinemedi");
    }
  };

  const saveCategory = async () => {
    const nextKey = String(categoryForm.key || "").trim();
    if (!nextKey) {
      message.error("Kategori adi zorunlu");
      return;
    }
    const nextCategories = categoryList
      .map(({ color, count, ...item }) => item)
      .filter((item) => item.key !== editingCategoryKey)
      .concat({
        key: nextKey,
        icon: categoryForm.icon || "📁",
        sla: categoryForm.sla || "4 saat",
        description: categoryForm.description || "",
      });
    try {
      await saveSupportSettings({ support_categories_json: nextCategories }, editingCategoryKey ? "Kategori guncellendi" : "Kategori eklendi");
      setCatModalOpen(false);
      setEditingCategoryKey("");
      setCategoryForm(DEFAULT_CATEGORY_FORM);
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Kategori kaydedilemedi");
    }
  };

  const deleteCategory = async () => {
    if (!editingCategoryKey) return;
    const nextCategories = categoryList
      .map(({ color, count, ...item }) => item)
      .filter((item) => item.key !== editingCategoryKey);
    try {
      await saveSupportSettings({ support_categories_json: nextCategories }, "Kategori silindi");
      setCatDeleteOpen(false);
      setEditingCategoryKey("");
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Kategori silinemedi");
    }
  };

  const saveCannedResponse = async () => {
    const nextTitle = String(cannedForm.title || "").trim();
    const nextText = String(cannedForm.text || "").trim();
    if (!nextTitle || !nextText) {
      message.error("Hazir yanit basligi ve metni zorunlu");
      return;
    }
    const nextId = editingCannedId || `cr-${Date.now()}`;
    const nextResponses = cannedResponses
      .map((item) => item)
      .filter((item) => item.id !== editingCannedId)
      .concat({
        id: nextId,
        title: nextTitle,
        category: cannedForm.category || "Genel",
        shortcut: cannedForm.shortcut || "",
        text: nextText,
        badge: cannedForm.badge || "bg-p",
      });
    try {
      await saveSupportSettings({ support_canned_responses_json: nextResponses }, editingCannedId ? "Hazir yanit guncellendi" : "Hazir yanit eklendi");
      setCrModalOpen(false);
      setEditingCannedId("");
      setCannedForm(DEFAULT_CANNED_FORM);
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Hazir yanit kaydedilemedi");
    }
  };

  const deleteCannedResponse = async () => {
    if (!editingCannedId) return;
    const nextResponses = cannedResponses.filter((item) => item.id !== editingCannedId);
    try {
      await saveSupportSettings({ support_canned_responses_json: nextResponses }, "Hazir yanit silindi");
      setCrDeleteOpen(false);
      setEditingCannedId("");
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Hazir yanit silinemedi");
    }
  };

  const saveSettings = async () => {
    try {
      setSettingsSaving(true);
      await saveSupportSettings({ support_panel_config_json: settingsForm }, "Ayarlar kaydedildi");
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || "Ayarlar kaydedilemedi");
    } finally {
      setSettingsSaving(false);
    }
  };

  const ensureTicketConversation = useCallback(async (ticket) => {
    if (!ticket) return null;
    let conversationId = ticket.conversation_id || null;
    let contactId = ticket.contact_id || null;

    if (!conversationId && contactId) {
      const startChatResponse = await axios.post(API_ENDPOINTS.waContacts.startChat, {
        contact_id: contactId,
      });
      conversationId = startChatResponse && startChatResponse.data && startChatResponse.data.data && startChatResponse.data.data.id;
    }

    if (!conversationId && !contactId && ticket.customer_phone) {
      const accountsResponse = await axios.post(API_ENDPOINTS.whatsappAccounts.list, {});
      const accounts = (accountsResponse && accountsResponse.data && accountsResponse.data.data) || [];
      const connectedAccount = accounts.find((account) => account && account.status === "connected" && account.id);
      if (!connectedAccount) {
        throw new Error("Bagli bir WhatsApp hesabi olmadan mesaj gonderilemez.");
      }

      const contactResponse = await axios.post(API_ENDPOINTS.waContacts.create, {
        whatsapp_account_id: connectedAccount.id,
        display_name: ticket.customer_name || (ticket.contact && ticket.contact.display_name) || "Musteri",
        phone_e164: ticket.customer_phone,
        wa_id: ticket.customer_phone,
      });
      const createdContact = contactResponse && contactResponse.data && contactResponse.data.data;
      contactId = createdContact && createdContact.id;

      if (contactId) {
        const startChatResponse = await axios.post(API_ENDPOINTS.waContacts.startChat, {
          contact_id: contactId,
        });
        conversationId = startChatResponse && startChatResponse.data && startChatResponse.data.data && startChatResponse.data.data.id;
      }
    }

    if (!conversationId) return null;

    if (!ticket.conversation_id || (!ticket.contact_id && contactId)) {
      await axios.post(API_ENDPOINTS.supportTickets.update, {
        id: ticket.id,
        conversation_id: conversationId,
        contact_id: contactId,
      });
    }

    return { conversationId, contactId };
  }, []);

  const sendReply = async () => {
    if (!selectedRow) return;
    if (!replyText.trim()) {
      message.error("Gonderilecek yanit bos olamaz");
      return;
    }
    try {
      const prepared = await ensureTicketConversation(selectedRow);
      if (!prepared || !prepared.conversationId) {
        message.error("Bu ticket icin konusma kaydi otomatik olusturulamadi.");
        return;
      }
      await axios.post(API_ENDPOINTS.messages.send, {
        conversation_id: prepared.conversationId,
        text_body: replyText.trim(),
      });
      await axios.post(API_ENDPOINTS.supportTickets.update, {
        id: selectedRow.id,
        resolution_text: replyText.trim(),
        status: "waiting_customer",
        first_response_at: selectedRow.first_response_at || new Date().toISOString(),
      });
      message.success("Yanit gonderildi");
      setReplyText("");
      setReplyPickerOpen(false);
      loadData();
    } catch (error) {
      message.error((error && error.response && error.response.data && error.response.data.message) || error.message || "Yanit gonderilemedi");
    }
  };

  const sendBulkInvoiceMessage = async () => {
    const invoiceTargets = rows.filter((row) => String(row.category || "").toLowerCase().includes("fatura"));
    const reachableFilteredTargets = filteredRows.filter((row) => row.conversation_id || row.contact_id || row.customer_phone);
    const targets = (invoiceTargets.length ? invoiceTargets : reachableFilteredTargets).slice(0, 25);
    if (!targets.length) {
      message.warning("Toplu mesaj gonderecek uygun ticket bulunamadi");
      return;
    }
    const textBody = invoiceTargets.length
      ? "Fatura talepleriniz inceleniyor. Siradaki tum kayitlar icin toplu durum guncellemesi paylasildi. Gelisme olursa ayrica bilgilendirecegiz."
      : "Destek talepleriniz inceleniyor. Uygun kayitlar icin toplu durum guncellemesi paylasildi. Gelisme olursa ayrica bilgilendirecegiz.";
    const results = await Promise.allSettled(
      targets.map(async (row) => {
        const prepared = await ensureTicketConversation(row);
        if (!prepared || !prepared.conversationId) throw new Error("conversation_missing");
        return axios.post(API_ENDPOINTS.messages.send, {
          conversation_id: prepared.conversationId,
          text_body: textBody,
        });
      })
    );
    const successCount = results.filter((item) => item.status === "fulfilled").length;
    if (!successCount) {
      message.error("Toplu bilgilendirme gonderilemedi");
      return;
    }
    message.success(`${successCount} ticket icin toplu bilgilendirme gonderildi`);
  };

  const downloadReport = () => {
    const today = new Date().toISOString().slice(0, 10);
    const headerRows = [
      ["Destek Raporu", today],
      ["Acik Ticket", stats && stats.open ? stats.open : rows.filter((row) => row.status === "open").length],
      ["Cozulen Bugun", report && report.resolved_today ? report.resolved_today : 0],
      ["Ort. Cozum Suresi", formatMinutes(report && report.avg_resolution_minutes)],
      ["CSAT", report && report.avg_csat_score ? report.avg_csat_score : "-"],
      [""],
      ["Ticket ID", "Musteri", "Konu", "Kategori", "Oncelik", "Durum", "SLA", "Atanan", "Olusturma"],
      ...filteredRows.map((row) => [
        `TK-${row.id}`,
        row.customer_name || (row.contact && row.contact.display_name) || "Musteri",
        row.subject || "—",
        row.category || "Genel",
        (PRIORITY_MAP[row.priority] || PRIORITY_MAP.medium).label,
        (STATUS_MAP[row.status] || STATUS_MAP.open).label,
        slaDisplayText(row),
        row.assigned_to || "Atanmamis",
        formatDateTime(row.created_at),
      ]),
    ];
    downloadCsv(headerRows, `destek-raporu-${today}.csv`);
    message.success("Destek raporu indirildi");
  };

  if (!accessMeta.loaded) {
    return <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--cp-muted)" }}>AI calisan paneli yukleniyor...</div>;
  }

  const customerTypeLabel = selectedRow && selectedRow.contact && selectedRow.contact.is_vip
    ? { label: "VIP", badge: "bg-p" }
    : selectedRow && selectedRow.contact && Number(selectedRow.contact.ticket_count || 0) >= 3
      ? { label: "Sadik", badge: "bg-b" }
      : { label: "Standart", badge: "bg-gr" };

  const resolvedToday = Number((report && report.resolved_today) || (stats && stats.resolved_today) || 0);
  const avgResolutionText = formatMinutes(report && report.avg_resolution_minutes);
  const avgFirstResponseText = formatMinutes(report && report.avg_first_response_minutes);
  const slaComplianceText = `${Number((report && report.sla_compliance) || 0)}%`;

  return (
    <>
      <style>{SUPPORT_PANEL_CSS}</style>
      <div className="sup-page">
        <div className="sup-shell">
          <div className="sup-hdr">
            <div className="sup-hdr-l">
              <div className="sup-av">🎧</div>
              <div>
                <h1>Destek Uzmani</h1>
                <p>Ticket yonetimi · SLA takibi · CSAT analizi · AI destekli cozum</p>
              </div>
            <BotStatusToggle botType="support" style={{ marginLeft: 12 }} />
            </div>
            <div className="sup-hdr-r">
              <button className="sup-btn" onClick={downloadReport}>📊 Rapor</button>
              <button className="sup-btn sup-btn-primary" onClick={openNew}>+ Yeni Ticket</button>
            </div>
          </div>

          <div className="sup-alert sup-alert-w">
            ⚠️ SLA Uyarisi: {slaBreachedCount} ticket SLA limitine yaklasti.{selectedRow ? ` TK-${selectedRow.id} icin kalan sure: ${slaDisplayText(selectedRow)}.` : ""}
            <span style={{ marginLeft: "auto", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }} onClick={() => selectedRow && setDetailOpen(true)}>Detay Gor →</span>
          </div>
          <div className="sup-alert sup-alert-s">
            🤖 AI Oneri: Son 1 saatte "fatura" konulu {invoiceTicketCount} ticket acildi. Uygun kayitlara toplu bilgilendirme gonderilebilir.
            <span style={{ marginLeft: "auto", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }} onClick={sendBulkInvoiceMessage}>Gonder →</span>
          </div>

          <div className="sup-stats">
            <div className="sup-stat">
              <div className="sup-stat-i">📨</div>
              <div className="sup-stat-v">{Number((stats && stats.open) || rows.filter((row) => row.status === "open").length || 0)}</div>
              <div className="sup-stat-l">Acik Ticket</div>
              <div className="sup-stat-c" style={{ color: urgentCount ? "var(--cp-warn)" : "var(--cp-muted)" }}>{urgentCount} acil oncelikli</div>
            </div>
            <div className="sup-stat">
              <div className="sup-stat-i">✅</div>
              <div className="sup-stat-v">{resolvedToday}</div>
              <div className="sup-stat-l">Cozulen Bugun</div>
              <div className="sup-stat-c">{formatSignedDelta(report && report.resolved_today_delta)} onceki doneme gore</div>
            </div>
            <div className="sup-stat">
              <div className="sup-stat-i">⏱️</div>
              <div className="sup-stat-v">{avgResolutionText}</div>
              <div className="sup-stat-l">Ort. Cozum Suresi</div>
              <div className="sup-stat-c">{avgFirstResponseText} ilk yanit</div>
            </div>
            <div className="sup-stat">
              <div className="sup-stat-i">⭐</div>
              <div className="sup-stat-v">{(report && report.avg_csat_score) || "—"}</div>
              <div className="sup-stat-l">CSAT Puani</div>
              <div className="sup-stat-c">{slaComplianceText} SLA uyum</div>
            </div>
          </div>

          <div className="sup-tabs">
            <div className={`sup-tab${activeTab === "tickets" ? " active" : ""}`} onClick={() => setActiveTab("tickets")}>📋 Ticket Listesi</div>
            <div className={`sup-tab${activeTab === "categories" ? " active" : ""}`} onClick={() => setActiveTab("categories")}>📁 Kategoriler</div>
            <div className={`sup-tab${activeTab === "canned" ? " active" : ""}`} onClick={() => setActiveTab("canned")}>💬 Hazir Yanitlar</div>
            <div className={`sup-tab${activeTab === "settings" ? " active" : ""}`} onClick={() => setActiveTab("settings")}>⚙️ Ayarlar</div>
          </div>

          {activeTab === "tickets" && (
            <div className="sup-g3">
              <div>
                <div className="sup-srch">
                  <input className="sup-input" placeholder="Ticket ara (ID, musteri, konu...)" value={search} onChange={(e) => setSearch(e.target.value)} />
                  <select className="sup-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">Tum Durumlar</option>
                    {Object.keys(STATUS_MAP).map((key) => <option key={key} value={key}>{STATUS_MAP[key].label}</option>)}
                  </select>
                  <select className="sup-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                    <option value="all">Tum Oncelikler</option>
                    {Object.keys(PRIORITY_MAP).map((key) => <option key={key} value={key}>{PRIORITY_MAP[key].label}</option>)}
                  </select>
                  <select className="sup-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    <option value="all">Tum Kategoriler</option>
                    {categoryList.map((category) => <option key={category.key} value={category.key}>{category.key}</option>)}
                  </select>
                </div>

                <div className="sup-card">
                  {loading ? (
                    <div className="sup-empty">Ticket listesi yukleniyor...</div>
                  ) : filteredRows.length ? (
                    <>
                      <div style={{ overflowX: "auto" }}>
                        <table className="sup-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Musteri</th>
                              <th>Konu</th>
                              <th>Oncelik</th>
                              <th>Durum</th>
                              <th>Kategori</th>
                              <th>SLA</th>
                              <th>Tarih</th>
                              <th>Islem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedRows.map((row) => {
                              const sMeta = STATUS_MAP[row.status] || STATUS_MAP.open;
                              const pMeta = PRIORITY_MAP[row.priority] || PRIORITY_MAP.medium;
                              return (
                                <tr
                                  key={row.id}
                                  className="sup-row-click"
                                  onClick={() => {
                                    setSelectedId(row.id);
                                    setDetailOpen(true);
                                  }}
                                >
                                  <td style={{ fontWeight: 700, color: "var(--cp-violet)" }}>TK-{row.id}</td>
                                  <td>{row.customer_name || (row.contact && row.contact.display_name) || "Musteri"}</td>
                                  <td>{row.subject || "—"}</td>
                                  <td><span className={`sup-badge ${pMeta.badge}`}>{pMeta.label}</span></td>
                                  <td><span className={`sup-badge ${sMeta.badge}`}>{sMeta.label}</span></td>
                                  <td>{row.category || "Genel"}</td>
                                  <td><span style={{ color: slaTextColor(row), fontWeight: 600, fontSize: "11px" }}>{slaDisplayText(row)}</span></td>
                                  <td style={{ color: "var(--cp-faint)", fontSize: "11px" }}>{formatDateTime(row.created_at)}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <button className="sup-btn sup-btn-sm" onClick={() => { setSelectedId(row.id); setDetailOpen(true); }}>👁️</button>{" "}
                                    <button className="sup-btn sup-btn-sm" onClick={() => openEdit(row)}>✏️</button>{" "}
                                    <button className="sup-btn sup-btn-sm sup-btn-danger" onClick={() => { setSelectedId(row.id); setTicketDeleteOpen(true); }}>🗑️</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="sup-pager">
                        <div className="sup-pager-meta">{filteredRows.length} kayit · Sayfa {safePage}/{totalPages}</div>
                        <div className="sup-pager-actions">
                          <button className="sup-btn sup-btn-sm" disabled={safePage <= 1} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}>Onceki</button>
                          <button className="sup-btn sup-btn-sm" disabled={safePage >= totalPages} onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>Sonraki</button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="sup-empty">Bu filtrede ticket bulunamadi.</div>
                  )}
                </div>
              </div>

              <div>
                <div className="sup-card">
                  <div className="sup-card-title"><span>🤖 AI Analiz</span></div>
                  <div className="sup-ai">
                    <div className="sup-ai-b">Otomatik Kategorizasyon</div>
                    <div className="sup-ai-t">{selectedRow ? `TK-${selectedRow.id} icin mevcut kategori "${selectedRow.category || "Genel"}". Gerekirse kategori ve atamayi duzenleyebilirsiniz.` : "Analiz icin bir ticket secin."}</div>
                    <div style={{ marginTop: 6 }}>
                      <button className="sup-btn sup-btn-sm sup-btn-primary" onClick={() => selectedRow && openEdit(selectedRow)}>Duzenle</button>
                    </div>
                  </div>
                  <div className="sup-ai">
                    <div className="sup-ai-b">Duygu Analizi</div>
                    <div className="sup-ai-t">Son 30 gunluk kayitlarda ortalama CSAT {(report && report.avg_csat_score) || "—"} seviyesinde. En yogun kategori: {(report && report.top_category) || "—"}.</div>
                  </div>
                  <div className="sup-ai">
                    <div className="sup-ai-b">SLA Risk Uyarisi</div>
                    <div className="sup-ai-t">{slaBreachedCount} ticket SLA ihlali riski tasiyor. Oncelik sirasina gore aksiyon alinmasi onerilir.</div>
                  </div>
                  <div className="sup-ai">
                    <div className="sup-ai-b">Onerilen Yanit</div>
                    <div className="sup-ai-t">{selectedRow ? `"${(selectedRow.customer_name || (selectedRow.contact && selectedRow.contact.display_name) || "Musteri")}, talebinizi inceliyoruz. Guncellemeyi birazdan paylasacagiz."` : "Hazir yanit secmek icin bir ticket secin."}</div>
                    <div style={{ marginTop: 6 }}>
                      <button className="sup-btn sup-btn-sm" onClick={() => selectedRow && setReplyText(`${selectedRow.customer_name || (selectedRow.contact && selectedRow.contact.display_name) || "Musteri"}, talebinizi inceliyoruz. Guncellemeyi birazdan paylasacagiz.`)}>Yanit Kutusuna Ekle</button>
                    </div>
                  </div>
                </div>

                <div className="sup-card">
                  <div className="sup-card-title"><span>📊 Bugunun Ozeti</span></div>
                  <div className="sup-dr"><span className="l">Toplam acilan</span><span className="v">{Number((stats && stats.total) || rows.length || 0)}</span></div>
                  <div className="sup-dr"><span className="l">Cozulen</span><span className="v">{Number((stats && stats.resolved) || 0)}</span></div>
                  <div className="sup-dr"><span className="l">Ilk yanit suresi (ort.)</span><span className="v">{avgFirstResponseText}</span></div>
                  <div className="sup-dr"><span className="l">SLA uyum orani</span><span className="v" style={{ color: "var(--cp-ok)" }}>{slaComplianceText}</span></div>
                  <div className="sup-dr"><span className="l">Eskalasyon</span><span className="v" style={{ color: "var(--cp-warn)" }}>{Number((report && report.escalations) || 0)}</span></div>
                  <div className="sup-dr"><span className="l">En yogun kategori</span><span className="v">{(report && report.top_category) || "—"}</span></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "categories" && (
            <div className="sup-g2">
              <div>
                <div className="sup-srch">
                  <input className="sup-input" placeholder="Kategori ara..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} />
                  <button className="sup-btn sup-btn-sm sup-btn-primary" onClick={() => openCategoryModal(null)}>+ Kategori Ekle</button>
                </div>
                <div className="sup-card">
                  <div className="sup-card-title"><span>📁 Ticket Kategorileri</span></div>
                  {filteredCategories.map((category) => (
                    <div key={category.key} className="sup-cat-item">
                      <div className="sup-cat-info">
                        <div className="sup-cat-icon">{category.icon}</div>
                        <div>
                          <div className="sup-cat-name">{category.key}</div>
                          <div className="sup-cat-count">{category.count} acik ticket · SLA: {category.sla}</div>
                          {category.description ? <div className="sup-note">{category.description}</div> : null}
                        </div>
                      </div>
                      <div className="sup-cat-actions">
                        <button className="sup-btn sup-btn-sm" onClick={() => openCategoryModal(category)}>✏️</button>
                        <button className="sup-btn sup-btn-sm sup-btn-danger" onClick={() => { setEditingCategoryKey(category.key); setCatDeleteOpen(true); }}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="sup-card">
                  <div className="sup-card-title"><span>🏷️ Etiketler</span></div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {tags.map((tag) => <span key={tag.label} className={`sup-badge ${tag.badge}`}>{tag.label}</span>)}
                  </div>
                </div>

                <div className="sup-card">
                  <div className="sup-card-title"><span>📊 Kategori Dagilimi</span></div>
                  {categoryList.length ? categoryList.map((category, index) => {
                    const total = Math.max(rows.length, 1);
                    const pct = Math.round(((category.count || 0) / total) * 100);
                    const color = CAT_COLORS[index % CAT_COLORS.length];
                    return (
                      <div key={category.key} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                          <span>{category.key}</span>
                          <span style={{ color, fontWeight: 600 }}>%{pct}</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,.05)" }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: color }} />
                        </div>
                      </div>
                    );
                  }) : <div className="sup-empty">Henüz kategori verisi yok</div>}
                </div>
              </div>
            </div>
          )}

          {activeTab === "canned" && (
            <div>
              <div className="sup-srch">
                <input className="sup-input" placeholder="Hazir yanit ara..." value={cannedSearch} onChange={(e) => setCannedSearch(e.target.value)} />
                <select className="sup-select" value={cannedFilter} onChange={(e) => setCannedFilter(e.target.value)}>
                  <option value="all">Tum Kategoriler</option>
                  {Array.from(new Set(cannedResponses.map((item) => item.category))).map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <button className="sup-btn sup-btn-primary" onClick={() => openCannedModal(null)}>+ Yeni Hazir Yanit</button>
              </div>
              <div className="sup-g2">
                {groupedCannedResponses.map((column, columnIndex) => (
                  <div key={columnIndex}>
                    {column.length ? column.map((item) => (
                      <div key={item.id} className="sup-cr-item">
                        <div className="sup-cr-title"><span>{item.title}</span><span className={`sup-badge ${item.badge}`}>{item.category}</span></div>
                        <div className="sup-cr-cat">Kisayol: {item.shortcut || "—"}</div>
                        <div className="sup-cr-text">{item.text}</div>
                        <div className="sup-cr-actions">
                          <button className="sup-btn sup-btn-sm" onClick={() => { navigator.clipboard.writeText(item.text).catch(() => {}); message.success("Yanit kopyalandi"); }}>📋 Kopyala</button>
                          <button className="sup-btn sup-btn-sm" onClick={() => openCannedModal(item)}>✏️ Duzenle</button>
                          <button className="sup-btn sup-btn-sm sup-btn-danger" onClick={() => { setEditingCannedId(item.id); setCrDeleteOpen(true); }}>🗑️</button>
                        </div>
                      </div>
                    )) : <div className="sup-empty">Bu kolonda gosterilecek hazir yanit yok.</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "settings" && (
            <>
              <div className="sup-set">
                <div className="sup-set-g">
                  <div className="sup-set-h">⏱️ SLA Sureleri</div>
                  <div className="sup-form-row">
                    <div className="sup-form-group">
                      <label className="sup-form-label">Acil (saat)</label>
                      <input className="sup-fi" type="number" min="1" value={settingsForm.slaHoursByPriority.urgent} onChange={(e) => setSettingsForm((prev) => ({ ...prev, slaHoursByPriority: { ...prev.slaHoursByPriority, urgent: Number(e.target.value || 1) } }))} />
                    </div>
                    <div className="sup-form-group">
                      <label className="sup-form-label">Yuksek (saat)</label>
                      <input className="sup-fi" type="number" min="1" value={settingsForm.slaHoursByPriority.high} onChange={(e) => setSettingsForm((prev) => ({ ...prev, slaHoursByPriority: { ...prev.slaHoursByPriority, high: Number(e.target.value || 1) } }))} />
                    </div>
                  </div>
                  <div className="sup-form-row">
                    <div className="sup-form-group">
                      <label className="sup-form-label">Normal (saat)</label>
                      <input className="sup-fi" type="number" min="1" value={settingsForm.slaHoursByPriority.medium} onChange={(e) => setSettingsForm((prev) => ({ ...prev, slaHoursByPriority: { ...prev.slaHoursByPriority, medium: Number(e.target.value || 1) } }))} />
                    </div>
                    <div className="sup-form-group">
                      <label className="sup-form-label">Dusuk (saat)</label>
                      <input className="sup-fi" type="number" min="1" value={settingsForm.slaHoursByPriority.low} onChange={(e) => setSettingsForm((prev) => ({ ...prev, slaHoursByPriority: { ...prev.slaHoursByPriority, low: Number(e.target.value || 1) } }))} />
                    </div>
                  </div>
                  <div className="sup-form-row">
                    <div className="sup-form-group">
                      <label className="sup-form-label">Ilk Yanit (dk)</label>
                      <input className="sup-fi" type="number" min="1" value={settingsForm.firstResponseMinutes} onChange={updateSettingsField("firstResponseMinutes", (value) => Number(value || 1))} />
                    </div>
                    <div className="sup-form-group">
                      <label className="sup-form-label">Eskalasyon Esigi (%)</label>
                      <input className="sup-fi" type="number" min="1" max="100" value={settingsForm.escalationPercent} onChange={updateSettingsField("escalationPercent", (value) => Number(value || 1))} />
                    </div>
                  </div>
                </div>

                <div className="sup-set-g">
                  <div className="sup-set-h">🔄 Otomatik Atama</div>
                  <div className="sup-set-r"><span className="sl">Otomatik atama aktif</span><Toggle value={settingsForm.autoAssign} onChange={(value) => setSettingsForm((prev) => ({ ...prev, autoAssign: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Round-robin dagilim</span><Toggle value={settingsForm.roundRobin} onChange={(value) => setSettingsForm((prev) => ({ ...prev, roundRobin: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Beceri bazli yonlendirme</span><Toggle value={settingsForm.skillRouting} onChange={(value) => setSettingsForm((prev) => ({ ...prev, skillRouting: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Yogunluk dengeleme</span><Toggle value={settingsForm.loadBalancing} onChange={(value) => setSettingsForm((prev) => ({ ...prev, loadBalancing: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">VIP onceliklendirme</span><Toggle value={settingsForm.vipPrioritization} onChange={(value) => setSettingsForm((prev) => ({ ...prev, vipPrioritization: value }))} /></div>
                </div>

                <div className="sup-set-g">
                  <div className="sup-set-h">🔔 Bildirim Ayarlari</div>
                  <div className="sup-set-r"><span className="sl">Yeni ticket bildirimi</span><Toggle value={settingsForm.notifyNewTicket} onChange={(value) => setSettingsForm((prev) => ({ ...prev, notifyNewTicket: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">SLA uyari bildirimi</span><Toggle value={settingsForm.notifySla} onChange={(value) => setSettingsForm((prev) => ({ ...prev, notifySla: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Eskalasyon bildirimi</span><Toggle value={settingsForm.notifyEscalation} onChange={(value) => setSettingsForm((prev) => ({ ...prev, notifyEscalation: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Musteri yanit bildirimi</span><Toggle value={settingsForm.notifyCustomerReply} onChange={(value) => setSettingsForm((prev) => ({ ...prev, notifyCustomerReply: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">CSAT bildirimi</span><Toggle value={settingsForm.notifyCsat} onChange={(value) => setSettingsForm((prev) => ({ ...prev, notifyCsat: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Gunluk ozet e-postasi</span><Toggle value={settingsForm.dailySummaryEmail} onChange={(value) => setSettingsForm((prev) => ({ ...prev, dailySummaryEmail: value }))} /></div>
                </div>

                <div className="sup-set-g">
                  <div className="sup-set-h">🕐 Calisma Saatleri</div>
                  <div className="sup-form-row">
                    <div className="sup-form-group">
                      <label className="sup-form-label">Hafta ici baslangic</label>
                      <input className="sup-fi" type="time" value={settingsForm.weekdayStart} onChange={updateSettingsField("weekdayStart")} />
                    </div>
                    <div className="sup-form-group">
                      <label className="sup-form-label">Hafta ici bitis</label>
                      <input className="sup-fi" type="time" value={settingsForm.weekdayEnd} onChange={updateSettingsField("weekdayEnd")} />
                    </div>
                  </div>
                  <div className="sup-form-row">
                    <div className="sup-form-group">
                      <label className="sup-form-label">Cumartesi baslangic</label>
                      <input className="sup-fi" type="time" value={settingsForm.saturdayStart} onChange={updateSettingsField("saturdayStart")} />
                    </div>
                    <div className="sup-form-group">
                      <label className="sup-form-label">Cumartesi bitis</label>
                      <input className="sup-fi" type="time" value={settingsForm.saturdayEnd} onChange={updateSettingsField("saturdayEnd")} />
                    </div>
                  </div>
                  <div className="sup-set-r"><span className="sl">Pazar kapali</span><Toggle value={settingsForm.sundayClosed} onChange={(value) => setSettingsForm((prev) => ({ ...prev, sundayClosed: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Mesai disi AI bot</span><Toggle value={settingsForm.afterHoursBot} onChange={(value) => setSettingsForm((prev) => ({ ...prev, afterHoursBot: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Tatil gunleri otomatik yanit</span><Toggle value={settingsForm.holidayAutoReply} onChange={(value) => setSettingsForm((prev) => ({ ...prev, holidayAutoReply: value }))} /></div>
                  <div className="sup-set-r"><span className="sl">Saat dilimi</span><span className="sv">{settingsForm.timezone}</span></div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button className="sup-btn sup-btn-primary" onClick={saveSettings} disabled={settingsSaving}>{settingsSaving ? "Kaydediliyor..." : "Ayarlari Kaydet"}</button>
              </div>
            </>
          )}
        </div>
      </div>

      <Drawer
        visible={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={selectedRow ? `TK-${selectedRow.id} · Ticket Detay` : "Ticket detayi"}
        width={500}
        className="sup-drawer"
        drawerStyle={{ background: "var(--cp-elevated)" }}
        bodyStyle={{ background: "var(--cp-elevated)" }}
      >
        {selectedRow ? (
          <div>
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              <span className={`sup-badge ${(STATUS_MAP[selectedRow.status] || STATUS_MAP.open).badge}`}>{(STATUS_MAP[selectedRow.status] || STATUS_MAP.open).label}</span>
              <span className={`sup-badge ${(PRIORITY_MAP[selectedRow.priority] || PRIORITY_MAP.medium).badge}`}>{(PRIORITY_MAP[selectedRow.priority] || PRIORITY_MAP.medium).label}</span>
              <span className="sup-badge bg-p">{selectedRow.category || "Genel"}</span>
            </div>

            <div className="sup-drw-s">
              <h3>SLA Durumu</h3>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: slaTextColor(selectedRow) }}>Kalan Sure: {slaDisplayText(selectedRow)}</span>
                <span style={{ color: "var(--cp-faint)" }}>SLA: {settingsForm.slaHoursByPriority[selectedRow.priority || "medium"] || settingsForm.slaHoursByPriority.medium} saat</span>
              </div>
              <div className="sup-sla-bar">
                <div className={`sup-sla-fill ${slaColorClass(slaPercent(selectedRow.resolution_due_at))}`} style={{ width: `${slaPercent(selectedRow.resolution_due_at)}%` }} />
              </div>
            </div>

            <div className="sup-drw-s">
              <h3>Musteri Bilgileri</h3>
              <div className="sup-dr"><span className="l">Ad Soyad</span><span className="v">{selectedRow.customer_name || (selectedRow.contact && selectedRow.contact.display_name) || "Musteri"}</span></div>
              <div className="sup-dr"><span className="l">E-posta</span><span className="v">{selectedRow.customer_email || (selectedRow.contact && selectedRow.contact.email) || "—"}</span></div>
              <div className="sup-dr"><span className="l">Telefon</span><span className="v">{selectedRow.customer_phone || (selectedRow.contact && (selectedRow.contact.phone_e164 || selectedRow.contact.wa_id)) || "—"}</span></div>
              <div className="sup-dr"><span className="l">Musteri Tipi</span><span className="v"><span className={`sup-badge ${customerTypeLabel.badge}`}>{customerTypeLabel.label}</span></span></div>
              <div className="sup-dr"><span className="l">Toplam Ticket</span><span className="v">{(selectedRow.contact && selectedRow.contact.ticket_count) || 1}</span></div>
              <div className="sup-dr"><span className="l">Ort. CSAT</span><span className="v" style={{ color: "var(--cp-ok)" }}>{(selectedRow.contact && selectedRow.contact.avg_csat) || "—"}</span></div>
            </div>

            <div className="sup-drw-s">
              <h3>Ticket Bilgileri</h3>
              <div className="sup-dr"><span className="l">Konu</span><span className="v">{selectedRow.subject || "—"}</span></div>
              <div className="sup-dr"><span className="l">Kategori</span><span className="v">{selectedRow.category || "Genel"}</span></div>
              <div className="sup-dr"><span className="l">Olusturulma</span><span className="v">{formatDateTime(selectedRow.created_at)}</span></div>
              <div className="sup-dr"><span className="l">Atanan</span><span className="v">{selectedRow.assigned_to || "Atanmamis"}</span></div>
              <div className="sup-dr"><span className="l">Etiketler</span><span className="v">{normalizeTags(selectedRow.tags_json).length ? normalizeTags(selectedRow.tags_json).map((tag) => <span key={tag} className="sup-badge bg-p" style={{ marginLeft: 4 }}>{tag}</span>) : "—"}</span></div>
            </div>

            <div className="sup-drw-s">
              <h3>AI Analiz</h3>
              <div className="sup-ai">
                <div className="sup-ai-b">Duygu Analizi</div>
                <div className="sup-ai-t">Musteri {selectedRow.sentiment_score ? `(skor: ${selectedRow.sentiment_score}/5)` : "(skor verisi yok)"} gorunuyor. Cozum odakli yanit onerilir.</div>
              </div>
              <div className="sup-ai">
                <div className="sup-ai-b">Onerilen Yanit</div>
                <div className="sup-ai-t">"{(selectedRow.customer_name || (selectedRow.contact && selectedRow.contact.display_name) || "Sayin Musteri")}, talebinizi inceliyoruz. Guncellemeyi en kisa surede paylasacagiz."</div>
                <div style={{ marginTop: 6 }}>
                  <button className="sup-btn sup-btn-sm sup-btn-primary" onClick={() => setReplyText(`${selectedRow.customer_name || (selectedRow.contact && selectedRow.contact.display_name) || "Sayin Musteri"}, talebinizi inceliyoruz. Guncellemeyi en kisa surede paylasacagiz.`)}>Yanit Kutusuna Ekle</button>
                </div>
              </div>
            </div>

            <div className="sup-drw-s">
              <h3>Konusma Gecmisi</h3>
              <div className="sup-conv">
                <div className="sup-msg sup-msg-c">
                  <div className="sup-msg-h"><span className="n" style={{ color: "var(--cp-info)" }}>{selectedRow.customer_name || (selectedRow.contact && selectedRow.contact.display_name) || "Musteri"}</span><span className="t">{formatDateTime(selectedRow.created_at).split(" ")[1] || ""}</span></div>
                  {selectedRow.description || "Destek talebi acildi."}
                </div>
                {selectedRow.resolution_text ? (
                  <div className="sup-msg sup-msg-a">
                    <div className="sup-msg-h"><span className="n">Destek Uzmani</span><span className="t"></span></div>
                    {selectedRow.resolution_text}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="sup-drw-s">
              <h3>Hizli Islemler</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <button className="sup-btn sup-btn-sm sup-btn-primary" onClick={() => updateStatus(selectedRow, "in_progress", "Durum islemde olarak guncellendi")}>Isleme Al</button>
                <button className="sup-btn sup-btn-sm sup-btn-warn" onClick={() => updateStatus(selectedRow, "waiting_customer", "Durum beklemede olarak guncellendi")}>Beklemeye Al</button>
                <button className="sup-btn sup-btn-sm sup-btn-green" onClick={() => updateStatus(selectedRow, "resolved", "Ticket cozuldu olarak isaretlendi")}>Cozuldu</button>
                <button className="sup-btn sup-btn-sm" onClick={() => escalateTicket(selectedRow)}>Eskale Et</button>
                <button className="sup-btn sup-btn-sm" onClick={() => { openEdit(selectedRow); setDetailOpen(false); }}>Duzenle</button>
                <button className="sup-btn sup-btn-sm sup-btn-danger" onClick={() => { setTicketDeleteOpen(true); setDetailOpen(false); }}>Sil</button>
              </div>
            </div>

            <div className="sup-drw-s">
              <h3>Yanit Gonder</h3>
              <div style={{ marginBottom: 8 }}>
                <textarea className="sup-textarea" placeholder="Musteriye yanitinizi yazin..." value={replyText} onChange={(e) => setReplyText(e.target.value)} />
              </div>
              {replyPickerOpen ? (
                <div style={{ marginBottom: 8 }}>
                  <select className="sup-select" style={{ width: "100%" }} onChange={(e) => {
                    const item = cannedResponses.find((row) => row.id === e.target.value);
                    if (item) setReplyText(item.text);
                  }}>
                    <option value="">Hazir yanit secin</option>
                    {cannedResponses.map((item) => <option key={item.id} value={item.id}>{item.title} · {item.category}</option>)}
                  </select>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 6 }}>
                <button className="sup-btn sup-btn-primary" onClick={sendReply}>Gonder</button>
                <button className="sup-btn" onClick={() => setReplyPickerOpen((prev) => !prev)}>Hazir Yanit</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="sup-empty">Detay gosterilecek ticket bulunamadi.</div>
        )}
      </Drawer>

      <Modal visible={newOpen} onCancel={() => { setNewOpen(false); setEditingTicketId(null); }} onOk={() => saveTicket("new")} okText="Olustur" cancelText="Iptal" title="Yeni Ticket Olustur" className="sup-modal">
        <div className="sup-form-row">
          <div className="sup-form-group">
            <label className="sup-form-label">Musteri Adi</label>
            <input className="sup-fi" value={form.customer_name} onChange={updateFormField("customer_name")} placeholder="Musteri adi girin" />
          </div>
          <div className="sup-form-group">
            <label className="sup-form-label">Musteri E-posta</label>
            <input className="sup-fi" value={form.customer_email} onChange={updateFormField("customer_email")} placeholder="E-posta adresi" />
          </div>
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Musteri Telefon</label>
          <input className="sup-fi" value={form.customer_phone} onChange={updateFormField("customer_phone")} placeholder="Telefon numarasi" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Konu</label>
          <input className="sup-fi" value={form.subject} onChange={updateFormField("subject")} placeholder="Ticket konusu" />
        </div>
        <div className="sup-form-row">
          <div className="sup-form-group">
            <label className="sup-form-label">Oncelik</label>
            <select className="sup-fs" value={form.priority} onChange={updateFormField("priority")}>
              {Object.keys(PRIORITY_MAP).map((key) => <option key={key} value={key}>{PRIORITY_MAP[key].label}</option>)}
            </select>
          </div>
          <div className="sup-form-group">
            <label className="sup-form-label">Kategori</label>
            <select className="sup-fs" value={form.category} onChange={updateFormField("category")}>
              <option value="">Kategori secin</option>
              {categoryList.map((category) => <option key={category.key} value={category.key}>{category.key}</option>)}
            </select>
          </div>
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Etiketler</label>
          <input className="sup-fi" value={form.tags_input} onChange={updateFormField("tags_input")} placeholder="Etiket girin (virgul ile ayirin)" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Aciklama</label>
          <textarea className="sup-ft" placeholder="Ticket detayini yazin..." value={form.description} onChange={updateFormField("description")} />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Atanan Uzman</label>
          <select className="sup-fs" value={form.assigned_user_id} onChange={updateFormField("assigned_user_id")}>
            <option value="">Otomatik Ata</option>
            {assignedExperts.map((user) => (
              <option key={user.id} value={user.id}>{user.full_name || user.email || `Kullanici ${user.id}`}</option>
            ))}
          </select>
        </div>
      </Modal>

      <Modal visible={editOpen} onCancel={() => { setEditOpen(false); setEditingTicketId(null); }} onOk={() => saveTicket("edit")} okText="Kaydet" cancelText="Iptal" title={editingTicketId ? `Ticket Duzenle — TK-${editingTicketId}` : "Ticket Duzenle"} className="sup-modal" forceRender>
        <div className="sup-form-row">
          <div className="sup-form-group">
            <label className="sup-form-label">Musteri Adi</label>
            <input className="sup-fi" value={form.customer_name} onChange={updateFormField("customer_name")} placeholder="Musteri adi" />
          </div>
          <div className="sup-form-group">
            <label className="sup-form-label">Musteri E-posta</label>
            <input className="sup-fi" value={form.customer_email} onChange={updateFormField("customer_email")} placeholder="Musteri e-posta" />
          </div>
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Musteri Telefon</label>
          <input className="sup-fi" value={form.customer_phone} onChange={updateFormField("customer_phone")} placeholder="Musteri telefon" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Konu</label>
          <input className="sup-fi" value={form.subject} onChange={updateFormField("subject")} placeholder="Ticket konusu" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Aciklama</label>
          <textarea className="sup-ft" placeholder="Ticket detayini guncelleyin..." value={form.description} onChange={updateFormField("description")} />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Durum</label>
          <select className="sup-fs" value={form.status} onChange={updateFormField("status")}>
            {Object.keys(STATUS_MAP).map((key) => <option key={key} value={key}>{STATUS_MAP[key].label}</option>)}
          </select>
        </div>
        <div className="sup-form-row">
          <div className="sup-form-group">
            <label className="sup-form-label">Oncelik</label>
            <select className="sup-fs" value={form.priority} onChange={updateFormField("priority")}>
              {Object.keys(PRIORITY_MAP).map((key) => <option key={key} value={key}>{PRIORITY_MAP[key].label}</option>)}
            </select>
          </div>
          <div className="sup-form-group">
            <label className="sup-form-label">Kategori</label>
            <select className="sup-fs" value={form.category} onChange={updateFormField("category")}>
              {categoryList.map((category) => <option key={category.key} value={category.key}>{category.key}</option>)}
            </select>
          </div>
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Atanan Uzman</label>
          <select className="sup-fs" value={form.assigned_user_id} onChange={updateFormField("assigned_user_id")}>
            <option value="">Atanmamis</option>
            {assignedExperts.map((user) => (
              <option key={user.id} value={user.id}>{user.full_name || user.email || `Kullanici ${user.id}`}</option>
            ))}
          </select>
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Etiketler</label>
          <input className="sup-fi" value={form.tags_input} onChange={updateFormField("tags_input")} placeholder="Etiketler" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Dahili Not</label>
          <textarea className="sup-ft" placeholder="Ekip icin dahili not ekleyin..." value={form.resolution_text} onChange={updateFormField("resolution_text")} />
        </div>
      </Modal>

      <Modal visible={ticketDeleteOpen} onCancel={() => setTicketDeleteOpen(false)} onOk={deleteTicket} okText="Sil" cancelText="Vazgec" title="Ticket Sil" className="sup-modal" okButtonProps={{ danger: true }}>
        <div style={{ textAlign: "center" }}>
          <div className="sup-del-i">🗑️</div>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Bu ticketi silmek istediginize emin misiniz?</p>
          <p style={{ fontSize: 12, color: "var(--cp-faint)" }}>Bu islem geri alinamaz.</p>
        </div>
      </Modal>

      <Modal visible={crModalOpen} onCancel={() => setCrModalOpen(false)} onOk={saveCannedResponse} okText="Kaydet" cancelText="Iptal" title="Hazir Yanit Ekle / Duzenle" className="sup-modal">
        <div className="sup-form-group">
          <label className="sup-form-label">Yanit Basligi</label>
          <input className="sup-fi" value={cannedForm.title} onChange={updateCannedField("title")} placeholder="Orn: Hosgeldiniz Mesaji" />
        </div>
        <div className="sup-form-row">
          <div className="sup-form-group">
            <label className="sup-form-label">Kategori</label>
            <select className="sup-fs" value={cannedForm.category} onChange={updateCannedField("category")}>
              {Array.from(new Set(["Genel", ...categoryList.map((item) => item.key), ...cannedResponses.map((item) => item.category)])).map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div className="sup-form-group">
            <label className="sup-form-label">Kisayol</label>
            <input className="sup-fi" value={cannedForm.shortcut} onChange={updateCannedField("shortcut")} placeholder="Orn: /hosgeldin" />
          </div>
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Yanit Metni</label>
          <textarea className="sup-ft" style={{ minHeight: 120 }} value={cannedForm.text} onChange={updateCannedField("text")} placeholder="Hazir yanit metnini yazin." />
        </div>
      </Modal>

      <Modal visible={catModalOpen} onCancel={() => setCatModalOpen(false)} onOk={saveCategory} okText="Kaydet" cancelText="Iptal" title="Kategori Ekle / Duzenle" className="sup-modal" width={420}>
        <div className="sup-form-group">
          <label className="sup-form-label">Kategori Adi</label>
          <input className="sup-fi" value={categoryForm.key} onChange={updateCategoryField("key")} placeholder="Orn: Fatura / Odeme" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Ikon (Emoji)</label>
          <input className="sup-fi" value={categoryForm.icon} onChange={updateCategoryField("icon")} placeholder="Orn: 💳" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">SLA Suresi</label>
          <input className="sup-fi" value={categoryForm.sla} onChange={updateCategoryField("sla")} placeholder="Orn: 2 saat" />
        </div>
        <div className="sup-form-group">
          <label className="sup-form-label">Aciklama</label>
          <textarea className="sup-ft" value={categoryForm.description} onChange={updateCategoryField("description")} placeholder="Kategori aciklamasi..." />
        </div>
      </Modal>

      <Modal visible={catDeleteOpen} onCancel={() => setCatDeleteOpen(false)} onOk={deleteCategory} okText="Sil" cancelText="Vazgec" title="Kategori Sil" className="sup-modal" okButtonProps={{ danger: true }}>
        <p style={{ color: "var(--cp-text2)", marginBottom: 0 }}>Secili kategori kayittan kaldirilacak. Ticketlar silinmez, sadece kategori listesi guncellenir.</p>
      </Modal>

      <Modal visible={crDeleteOpen} onCancel={() => setCrDeleteOpen(false)} onOk={deleteCannedResponse} okText="Sil" cancelText="Vazgec" title="Hazir Yanit Sil" className="sup-modal" okButtonProps={{ danger: true }}>
        <p style={{ color: "var(--cp-text2)", marginBottom: 0 }}>Secili hazir yanit kayittan kaldirilacak.</p>
      </Modal>
    </>
  );
};

export default SupportTickets;
